import {
    AppBar,
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    IconButton,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Popper,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    CanAccess,
    LoadingIndicator,
    useGetIdentity,
    useGetList,
    useLogout,
} from 'react-admin';
import { Link, useNavigate } from 'react-router-dom';
import { useConfigurationContext } from '../root/ConfigurationContext';
import { useSiteStatus } from '../root/SiteStatusContext';
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Menu as MenuIcon,
    Search,
    Plus,
    UserPlus,
    Building2,
    Bug,
    FileText,
    HelpCircle,
    Settings,
    Users,
    LogOut,
    ChevronDown,
    Shield,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { AccountCreateDialog } from '../accounts/AccountCreateDialog';
import { ContactCreateDialog } from '../contacts/ContactCreateDialog';
import BugReportDialog from '../bugs/BugReportDialog';
import NotificationBell from '../bugs/NotificationBell';

const SITE_STATUS_CHIP_SX: Record<string, { bgcolor: string; color: string }> =
    {
        Live: { bgcolor: '#d1fae5', color: '#065f46' },
        'System Update': { bgcolor: '#f8d7da', color: '#721c24' },
        Beta: { bgcolor: '#fff3cd', color: '#856404' },
    };

interface HeaderProps {
    onMenuClick?: () => void;
}

const Header = ({ onMenuClick }: HeaderProps) => {
    const { logo, title } = useConfigurationContext();
    const { siteStatus } = useSiteStatus();
    const { identity } = useGetIdentity();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const logout = useLogout();
    const navigate = useNavigate();

    const [createAnchorEl, setCreateAnchorEl] = useState<null | HTMLElement>(
        null
    );
    const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [accountDialogOpen, setAccountDialogOpen] = useState(false);
    const [bugDialogOpen, setBugDialogOpen] = useState(false);
    const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
    const [capturedFormData, setCapturedFormData] = useState<Record<
        string,
        unknown
    > | null>(null);

    // Global search state
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const { data: accountResults, isLoading: accountsLoading } = useGetList(
        'accounts',
        {
            pagination: { page: 1, perPage: 5 },
            sort: { field: 'name', order: 'ASC' },
            filter: { q: debouncedQuery },
        },
        { enabled: debouncedQuery.length >= 2 }
    );

    const { data: contactResults, isLoading: contactsLoading } = useGetList(
        'contacts',
        {
            pagination: { page: 1, perPage: 5 },
            sort: { field: 'last_name', order: 'ASC' },
            filter: { q: debouncedQuery },
        },
        { enabled: debouncedQuery.length >= 2 }
    );

    const { data: quoteResults, isLoading: quotesLoading } = useGetList(
        'quotes',
        {
            pagination: { page: 1, perPage: 5 },
            sort: { field: 'name', order: 'ASC' },
            filter: { q: debouncedQuery },
        },
        { enabled: debouncedQuery.length >= 2 }
    );

    const isSearching = accountsLoading || contactsLoading || quotesLoading;
    const hasResults =
        (accountResults?.length ?? 0) > 0 ||
        (contactResults?.length ?? 0) > 0 ||
        (quoteResults?.length ?? 0) > 0;
    const showDropdown = searchOpen && debouncedQuery.length >= 2;

    const handleSearchSelect = (resource: string, id: string | number) => {
        setSearchOpen(false);
        setSearchQuery('');
        setDebouncedQuery('');
        navigate(`/${resource}/${id}/show`);
    };

    const handleBugReport = async () => {
        // Capture screenshot BEFORE opening dialog
        try {
            const canvas = await html2canvas(document.body, {
                useCORS: true,
                logging: false,
                scale: 1,
            });
            canvas.toBlob(blob => {
                setScreenshotBlob(blob);
            }, 'image/png');
        } catch {
            console.warn('Screenshot capture failed');
            setScreenshotBlob(null);
        }

        // Capture form data from HPT / Quote pages
        const formData: Record<string, unknown> = {};
        const url = window.location.hash || window.location.pathname;

        if (url.includes('hail-pricing-tool') || url.includes('quotes')) {
            // Grab all visible input/select values from the page
            const inputs = document.querySelectorAll<HTMLInputElement>(
                'input, select, textarea'
            );
            inputs.forEach(el => {
                const name =
                    el.name || el.id || el.getAttribute('aria-label') || '';
                if (name && el.value) {
                    formData[name] = el.value;
                }
            });

            // Also try to grab MUI Select values (rendered as divs)
            const muiSelects = document.querySelectorAll('[role="combobox"]');
            muiSelects.forEach(el => {
                const label =
                    el.getAttribute('aria-labelledby') ||
                    el.getAttribute('aria-label') ||
                    '';
                const value = el.textContent?.trim() || '';
                if (label && value) {
                    formData[`select_${label}`] = value;
                }
            });
        }

        setCapturedFormData(Object.keys(formData).length > 0 ? formData : null);
        setBugDialogOpen(true);
    };

    const isAdmin = identity?.administrator === true;
    const chipSx = SITE_STATUS_CHIP_SX[siteStatus] ?? {};
    const userInitials = identity
        ? `${(identity.first_name || '')[0] || ''}${(identity.last_name || '')[0] || ''}`.toUpperCase()
        : '';
    const fullName = identity
        ? `${identity.first_name || ''} ${identity.last_name || ''}`.trim()
        : '';

    return (
        <AppBar
            position="fixed"
            color="primary"
            sx={{
                width: '100%',
                zIndex: theme => theme.zIndex.drawer + 1,
                backgroundColor: '#ffffff',
                color: '#0f172a',
                borderBottom: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            }}
        >
            <Toolbar
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    minHeight: '64px !important',
                }}
            >
                {/* Left: Mobile menu + Logo */}
                <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                    {isMobile && (
                        <IconButton
                            aria-label="open drawer"
                            onClick={onMenuClick}
                            edge="start"
                            sx={{ mr: 1, color: '#475569' }}
                        >
                            <MenuIcon size={20} />
                        </IconButton>
                    )}

                    <Box
                        display="flex"
                        alignItems="center"
                        component={Link}
                        to="/"
                        sx={{
                            color: 'inherit',
                            textDecoration: 'inherit',
                        }}
                    >
                        {logo ? (
                            <img
                                src={logo}
                                alt={`${title} logo`}
                                style={{
                                    height: '36px',
                                    marginRight: '8px',
                                }}
                            />
                        ) : (
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    mr: 1,
                                }}
                            >
                                {title}
                            </Typography>
                        )}
                    </Box>

                    {/* Site status - small chip next to logo */}
                    {isAdmin && (
                        <Chip
                            label={siteStatus}
                            size="small"
                            sx={{
                                ...chipSx,
                                ml: 1,
                                fontSize: '0.7rem',
                                height: '22px',
                            }}
                        />
                    )}
                </Box>

                {/* Center: Global Search */}
                {!isMobile && (
                    <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
                        <Box
                            ref={searchRef}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                maxWidth: 480,
                                mx: 'auto',
                                position: 'relative',
                            }}
                        >
                            <TextField
                                placeholder="Search accounts, contacts, quotes..."
                                size="small"
                                fullWidth
                                value={searchQuery}
                                onChange={e => {
                                    setSearchQuery(e.target.value);
                                    setSearchOpen(true);
                                }}
                                onFocus={() => {
                                    if (searchQuery.length >= 2)
                                        setSearchOpen(true);
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={16} color="#94a3b8" />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e2e8f0',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline':
                                            {
                                                borderColor: '#cbd5e1',
                                            },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                            {
                                                borderColor: '#10b981',
                                            },
                                    },
                                }}
                            />
                            <Popper
                                open={showDropdown}
                                anchorEl={searchRef.current}
                                placement="bottom-start"
                                sx={{
                                    zIndex: 1301,
                                    width: searchRef.current?.offsetWidth,
                                }}
                            >
                                <Paper
                                    elevation={8}
                                    sx={{
                                        mt: 0.5,
                                        borderRadius: '8px',
                                        maxHeight: 400,
                                        overflow: 'auto',
                                        border: '1px solid #e2e8f0',
                                    }}
                                >
                                    {isSearching && (
                                        <Box sx={{ p: 2 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8125rem',
                                                    color: '#94a3b8',
                                                }}
                                            >
                                                Searching...
                                            </Typography>
                                        </Box>
                                    )}
                                    {!isSearching && !hasResults && (
                                        <Box sx={{ p: 2 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8125rem',
                                                    color: '#94a3b8',
                                                }}
                                            >
                                                No results found
                                            </Typography>
                                        </Box>
                                    )}
                                    {(accountResults?.length ?? 0) > 0 && (
                                        <>
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    pt: 1.5,
                                                    pb: 0.5,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.6875rem',
                                                        fontWeight: 600,
                                                        color: '#94a3b8',
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Accounts
                                                </Typography>
                                            </Box>
                                            {accountResults!.map(account => (
                                                <MenuItem
                                                    key={`a-${account.id}`}
                                                    onClick={() =>
                                                        handleSearchSelect(
                                                            'accounts',
                                                            account.id
                                                        )
                                                    }
                                                    sx={{
                                                        py: 1,
                                                        px: 2,
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: 32,
                                                        }}
                                                    >
                                                        <Building2
                                                            size={16}
                                                            color="#64748b"
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={account.name}
                                                        secondary={[
                                                            account.city,
                                                            account.stateAbbr,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(', ')}
                                                        primaryTypographyProps={{
                                                            fontSize:
                                                                '0.8125rem',
                                                            fontWeight: 500,
                                                        }}
                                                        secondaryTypographyProps={{
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </>
                                    )}
                                    {(accountResults?.length ?? 0) > 0 &&
                                        (contactResults?.length ?? 0) > 0 && (
                                            <Divider />
                                        )}
                                    {(contactResults?.length ?? 0) > 0 && (
                                        <>
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    pt: 1.5,
                                                    pb: 0.5,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.6875rem',
                                                        fontWeight: 600,
                                                        color: '#94a3b8',
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Contacts
                                                </Typography>
                                            </Box>
                                            {contactResults!.map(contact => (
                                                <MenuItem
                                                    key={`c-${contact.id}`}
                                                    onClick={() =>
                                                        handleSearchSelect(
                                                            'contacts',
                                                            contact.id
                                                        )
                                                    }
                                                    sx={{
                                                        py: 1,
                                                        px: 2,
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: 32,
                                                        }}
                                                    >
                                                        <Users
                                                            size={16}
                                                            color="#64748b"
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={`${contact.first_name} ${contact.last_name}`}
                                                        secondary={
                                                            contact.title ||
                                                            contact.account_name ||
                                                            ''
                                                        }
                                                        primaryTypographyProps={{
                                                            fontSize:
                                                                '0.8125rem',
                                                            fontWeight: 500,
                                                        }}
                                                        secondaryTypographyProps={{
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </>
                                    )}
                                    {((contactResults?.length ?? 0) > 0 ||
                                        (accountResults?.length ?? 0) > 0) &&
                                        (quoteResults?.length ?? 0) > 0 && (
                                            <Divider />
                                        )}
                                    {(quoteResults?.length ?? 0) > 0 && (
                                        <>
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    pt: 1.5,
                                                    pb: 0.5,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.6875rem',
                                                        fontWeight: 600,
                                                        color: '#94a3b8',
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Quotes
                                                </Typography>
                                            </Box>
                                            {quoteResults!.map(quote => (
                                                <MenuItem
                                                    key={`q-${quote.id}`}
                                                    onClick={() =>
                                                        handleSearchSelect(
                                                            'quotes',
                                                            quote.id
                                                        )
                                                    }
                                                    sx={{
                                                        py: 1,
                                                        px: 2,
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: 32,
                                                        }}
                                                    >
                                                        <FileText
                                                            size={16}
                                                            color="#64748b"
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={quote.name}
                                                        secondary={
                                                            quote.stage || ''
                                                        }
                                                        primaryTypographyProps={{
                                                            fontSize:
                                                                '0.8125rem',
                                                            fontWeight: 500,
                                                        }}
                                                        secondaryTypographyProps={{
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </>
                                    )}
                                </Paper>
                            </Popper>
                        </Box>
                    </ClickAwayListener>
                )}

                {/* Spacer for mobile */}
                {isMobile && <Box sx={{ flex: 1 }} />}

                {/* Right: Actions */}
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ flexShrink: 0 }}
                >
                    <LoadingIndicator />

                    {/* Quick Create Button */}
                    <IconButton
                        onClick={e => setCreateAnchorEl(e.currentTarget)}
                        sx={{
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                            width: 32,
                            height: 32,
                            '&:hover': {
                                backgroundColor: '#059669',
                            },
                        }}
                    >
                        <Plus size={16} />
                    </IconButton>
                    <Menu
                        anchorEl={createAnchorEl}
                        open={Boolean(createAnchorEl)}
                        onClose={() => setCreateAnchorEl(null)}
                        onClick={() => setCreateAnchorEl(null)}
                        PaperProps={{
                            sx: {
                                borderRadius: '8px',
                                mt: 1,
                                minWidth: 180,
                            },
                        }}
                    >
                        <MenuItem onClick={() => setContactDialogOpen(true)}>
                            <ListItemIcon>
                                <UserPlus size={16} color="#475569" />
                            </ListItemIcon>
                            <ListItemText
                                primary="New Contact"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                }}
                            />
                        </MenuItem>
                        <MenuItem onClick={() => setAccountDialogOpen(true)}>
                            <ListItemIcon>
                                <Building2 size={16} color="#475569" />
                            </ListItemIcon>
                            <ListItemText
                                primary="New Account"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                }}
                            />
                        </MenuItem>
                    </Menu>

                    {/* Bug Report */}
                    <Tooltip title="Report a bug">
                        <IconButton
                            onClick={handleBugReport}
                            sx={{ color: '#64748b' }}
                        >
                            <Bug size={20} />
                        </IconButton>
                    </Tooltip>

                    {/* Notification Bell */}
                    <NotificationBell />

                    {/* User Profile Dropdown */}
                    <Box
                        component="button"
                        onClick={e => setUserAnchorEl(e.currentTarget)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            px: 1,
                            py: 0.5,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                            },
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#10b981',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                            }}
                        >
                            {userInitials}
                        </Avatar>
                        {!isMobile && (
                            <Box
                                sx={{
                                    textAlign: 'left',
                                    lineHeight: 1.2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: 500,
                                        color: '#0f172a',
                                    }}
                                >
                                    {fullName}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.6875rem',
                                        color: '#64748b',
                                        textTransform: 'capitalize',
                                    }}
                                >
                                    {isAdmin ? 'Administrator' : 'Agent'}
                                </Typography>
                            </Box>
                        )}
                        <ChevronDown size={14} color="#94a3b8" />
                    </Box>
                    <Menu
                        anchorEl={userAnchorEl}
                        open={Boolean(userAnchorEl)}
                        onClose={() => setUserAnchorEl(null)}
                        PaperProps={{
                            sx: {
                                borderRadius: '8px',
                                mt: 1,
                                minWidth: 220,
                            },
                        }}
                    >
                        {/* User info header */}
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        color: '#0f172a',
                                    }}
                                >
                                    {fullName}
                                </Typography>
                                <Chip
                                    label="Active"
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        backgroundColor: '#dcfce7',
                                        color: '#166534',
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#64748b',
                                    textTransform: 'capitalize',
                                }}
                            >
                                {isAdmin ? 'Administrator' : 'Agent'}
                            </Typography>
                        </Box>
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                setUserAnchorEl(null);
                                navigate('/settings');
                            }}
                        >
                            <ListItemIcon>
                                <Settings size={16} color="#475569" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Profile Settings"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                }}
                            />
                        </MenuItem>
                        <CanAccess resource="admin-panel" action="list">
                            <MenuItem
                                onClick={() => {
                                    setUserAnchorEl(null);
                                    navigate('/admin-panel');
                                }}
                            >
                                <ListItemIcon>
                                    <Shield size={16} color="#475569" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Admin Dashboard"
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                    }}
                                />
                            </MenuItem>
                        </CanAccess>
                        <MenuItem
                            component={Link}
                            to="/support"
                            onClick={() => setUserAnchorEl(null)}
                        >
                            <ListItemIcon>
                                <HelpCircle size={16} color="#475569" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Support"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                }}
                            />
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                setUserAnchorEl(null);
                                logout();
                            }}
                        >
                            <ListItemIcon>
                                <LogOut size={16} color="#ef4444" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Sign out"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    color: '#ef4444',
                                }}
                            />
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
            <AccountCreateDialog
                open={accountDialogOpen}
                onClose={() => setAccountDialogOpen(false)}
            />
            <ContactCreateDialog
                open={contactDialogOpen}
                onClose={() => setContactDialogOpen(false)}
            />
            <BugReportDialog
                open={bugDialogOpen}
                onClose={() => {
                    setBugDialogOpen(false);
                    setScreenshotBlob(null);
                    setCapturedFormData(null);
                }}
                screenshotBlob={screenshotBlob}
                formData={capturedFormData}
            />
        </AppBar>
    );
};

export default Header;
